import { Empty, Flex, Group, IconFont, Modal, Popover, Text } from '@byecode/ui'
import type { ArgumentsSchema } from '@lighthouse/core'
import { nanoid, stopPropagation } from '@lighthouse/tools'
import React, { useRef, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { PopoverQuestion } from '@/components/PopoverQuestion'

import type { PageParameterFormSlice } from '../formSlice'
import { ParameterFormDropdown } from './components/ParameterFormDropdown'
import { ParameterItem } from './components/ParameterItem'

const Link = styled.a`
    display: inline-block;
    color: var(--color-main);
    line-height: 22px;
`

export const QueryParameter = () => {
    const { control } = useFormContext<PageParameterFormSlice>()
    const { fields, append, remove, update } = useFieldArray({
        control,
        name: 'parameterAndVariable.queryParameter',
        keyName: 'key'
    })

    const countRef = useRef(fields.length)

    const getNewName = (): string => {
        const key = (++countRef.current).toString().padStart(2, '0')
        const name = `query_data${key}`
        if (fields.some(item => item.name === name)) {
            return getNewName()
        }

        return name
    }

    const [addedParameter, setAddedParameter] = useState<ArgumentsSchema | null>(null)

    return (
        <Group
            label={
                <PopoverQuestion
                    type="pageRouteQuery"
                    renderTarget={(node, ref) => (
                        <Flex alignItems="center" gap={6} ref={ref}>
                            <IconFont type="QuestionSquare" size={16} fill="var(--color-gray-400)" />
                            <Text>查询参数</Text>
                            {node}
                        </Flex>
                    )}
                />
            }
            extra={
                <Popover
                    width={324}
                    withinPortal
                    position="left-start"
                    autoUpdate={false}
                    offsetOptions={294}
                    onOpen={() => {
                        const id = nanoid()
                        const name = getNewName()
                        const innerType = 'TEXT'
                        setAddedParameter({ id, name, innerType })
                        append({ id, name, innerType })
                    }}
                    onClose={() => {
                        setAddedParameter(null)
                    }}
                >
                    <Popover.Target>
                        <IconFont type="Add" onClick={stopPropagation} />
                    </Popover.Target>
                    {addedParameter && (
                        <ParameterFormDropdown
                            data={addedParameter}
                            existingNames={fields.filter(f => f.id !== addedParameter.id).map(f => f.name)}
                            onUpdateItem={data => {
                                // 名称重复
                                if (fields.some(f => f.name === data.name && f.id !== data.id)) {
                                    return
                                }

                                const index = fields.findIndex(item => item.id === data.id)
                                const old = fields[index]
                                if (old.innerType === data.innerType && old.name === data.name) {
                                    return
                                }
                                update(index, data)
                            }}
                        />
                    )}
                </Popover>
            }
            styles={{
                root: {
                    padding: 0,
                    overflow: 'hidden',
                    borderRadius: 8,
                    border: '1px solid var(--color-gray-200)'
                },
                label: {
                    fontWeight: 400
                },
                wrapper: {
                    backgroundColor: 'var(--color-gray-200)'
                },
                collapse: {
                    padding: '8px 16px !important'
                }
            }}
        >
            {fields.length === 0 ? (
                <Empty
                    icon="QuestionSquare"
                    title={
                        <>
                            <Text>关于查询参数的介绍</Text>
                            <Link
                                href="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/ggwyknifql34obdi"
                                target="_blank"
                                rel="noreferrer"
                            >
                                {'了解更多>'}
                            </Link>
                        </>
                    }
                    styles={{
                        title: {
                            textAlign: 'center',
                            fontWeight: '400 !important',
                            color: 'var(--color-gray-400)'
                        },
                        wrapper: {
                            padding: 12
                        }
                    }}
                />
            ) : (
                fields.map((item, index) => (
                    <ParameterItem
                        key={item.id}
                        data={item}
                        existingNames={fields.filter(f => f.id !== item.id).map(f => f.name)}
                        onUpdateItem={data => {
                            if (fields.some(f => f.name === data.name && f.id !== data.id)) {
                                return
                            }
                            update(index, data)
                        }}
                        onDeleteItem={async () => {
                            const isConfirm = await Modal.confirm({
                                title: `确认删除“${item.name}”？`,
                                content: '删除后，将影响与该字段相关的所有设置和数据。请确认是否继续。'
                            })
                            if (isConfirm) {
                                remove(index)
                            }
                        }}
                    />
                ))
            )}
        </Group>
    )
}
